import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import '../styles/privacy-policy.css';

const sections = [
  { id: 'introduction', title: '1. Introduction' },
  { id: 'information-we-collect', title: '2. Information We Collect' },
  { id: 'how-we-use', title: '3. How We Use Your Information' },
  { id: 'sms-policy', title: '4. SMS/Text Messaging Policy' },
  { id: 'how-we-share', title: '5. How We Share Your Information' },
  { id: 'data-security', title: '6. Data Security' },
  { id: 'data-retention', title: '7. Data Retention' },
  { id: 'third-party', title: '8. Third-Party Links' },
  { id: 'childrens-privacy', title: "9. Children's Privacy" },
  { id: 'changes', title: '10. Changes to This Privacy Policy' },
  { id: 'contact-us', title: '11. Contact Us' },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const handleNavClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="privacy-page">
      <Navbar />

      {/* Hero Section */}
      <section className="privacy-hero">
        <div className="privacy-hero-overlay">
          <div className="privacy-hero-content">
            <p className="privacy-hero-eyebrow">Legal &amp; Compliance</p>
            <h1 className="privacy-hero-title">Privacy Policy</h1>
            <p className="privacy-hero-subtitle">
              All Seasons Landscaping, Inc.
            </p>
            <div className="privacy-effective-date">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Effective Date: April 2, 2026
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="privacy-layout">

        {/* Sidebar TOC */}
        <aside className="privacy-sidebar">
          <div className="sidebar-sticky">
            <h4 className="sidebar-title">Contents</h4>
            <nav className="sidebar-nav">
              {sections.map((s) => (
                <button
                  key={s.id}
                  className={`sidebar-link ${activeSection === s.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(s.id)}
                >
                  {s.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        {/* Policy Body */}
        <main className="privacy-body">

          {/* Section 1 */}
          <section id="introduction" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">01</span>
              Introduction
            </h2>
            <p>
              All Seasons Landscaping, Inc. ("Company," "we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you visit our website (<a href="https://www.allseasons.info" className="policy-link" target="_blank" rel="noopener noreferrer">www.allseasons.info</a>), use our services, or communicate with us via phone, email, or text message (SMS/MMS).
            </p>
            <p>
              By using our services, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access our website or use our services.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 2 */}
          <section id="information-we-collect" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">02</span>
              Information We Collect
            </h2>

            <h3 className="policy-subsection-title">2.1 Personal Information</h3>
            <p>
              We may collect personally identifiable information that you voluntarily provide to us, including but not limited to: your name, mailing address, email address, phone number, and property address. This information is collected when you request a service estimate, enter into a service agreement, contact us with inquiries, or opt in to receive text message notifications.
            </p>

            <h3 className="policy-subsection-title">2.2 Automatically Collected Information</h3>
            <p>
              When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, referring URLs, and browsing behavior. This information is used to improve our website and services.
            </p>

            <h3 className="policy-subsection-title">2.3 SMS/Text Message Data</h3>
            <p>
              When you opt in to receive text messages from us, we collect your mobile phone number and records of messages sent and received. We use this information solely to deliver service notifications, appointment reminders, and crew arrival updates related to work being performed at your property.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 3 */}
          <section id="how-we-use" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">03</span>
              How We Use Your Information
            </h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="policy-list">
              <li>To provide, operate, and maintain our landscaping, maintenance, and snow removal services</li>
              <li>To send you service-related communications including scheduling confirmations, crew notifications, and appointment reminders via email, phone, or SMS</li>
              <li>To process your transactions and manage your account</li>
              <li>To respond to your inquiries and provide customer support</li>
              <li>To improve our website, services, and customer experience</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <div className="section-divider" />

          {/* Section 4 */}
          <section id="sms-policy" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">04</span>
              SMS/Text Messaging Policy
            </h2>

            <h3 className="policy-subsection-title">4.1 Consent</h3>
            <p>
              By providing your mobile phone number and opting in to text message communications, you consent to receive service-related text messages from All Seasons Landscaping. Message frequency varies based on your service schedule. Message and data rates may apply.
            </p>

            <h3 className="policy-subsection-title">4.2 Message Content</h3>
            <p>Text messages from All Seasons Landscaping will be limited to:</p>
            <ul className="policy-list">
              <li>Service scheduling notifications (e.g., crew arrival times, service day reminders)</li>
              <li>Appointment confirmations and updates</li>
              <li>Weather-related service delays or rescheduling notices</li>
              <li>Account-related communications</li>
            </ul>

            <h3 className="policy-subsection-title">4.3 Opt-Out</h3>
            <p>
              You may opt out of receiving text messages at any time by replying <strong>STOP</strong> to any message. After opting out, you will receive a one-time confirmation message. You may also opt out by contacting us at <a href="mailto:matt@allseasons.info" className="policy-link">matt@allseasons.info</a> or <a href="tel:2088068440" className="policy-link">(208) 806-8440</a>.
            </p>

            <h3 className="policy-subsection-title">4.4 Help</h3>
            <p>
              For help with text messaging, reply <strong>HELP</strong> to any message or contact us at <a href="mailto:matt@allseasons.info" className="policy-link">matt@allseasons.info</a> or <a href="tel:2088068440" className="policy-link">(208) 806-8440</a>.
            </p>

            <h3 className="policy-subsection-title">4.5 No Sharing of SMS Data</h3>
            <div className="policy-highlight-box">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>
                We do not sell, rent, share, or otherwise disclose your phone number or any SMS opt-in data to third parties for marketing or promotional purposes. SMS consent and phone numbers collected for SMS purposes will not be shared with any third parties or affiliates for their marketing use.
              </p>
            </div>
          </section>

          <div className="section-divider" />

          {/* Section 5 */}
          <section id="how-we-share" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">05</span>
              How We Share Your Information
            </h2>
            <p>
              We do not sell your personal information to third parties. We may share your information with trusted service providers who assist us in operating our business (such as our business management software, payment processors, and communication platforms), but only to the extent necessary for them to perform services on our behalf. We may also disclose your information if required by law, regulation, or legal process.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 6 */}
          <section id="data-security" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">06</span>
              Data Security
            </h2>
            <p>
              We implement commercially reasonable administrative, technical, and physical security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 7 */}
          <section id="data-retention" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">07</span>
              Data Retention
            </h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide you services, comply with our legal obligations, resolve disputes, and enforce our agreements. SMS message logs are retained for a reasonable period necessary for business and compliance purposes.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 8 */}
          <section id="third-party" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">08</span>
              Third-Party Links
            </h2>
            <p>
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of those websites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 9 */}
          <section id="childrens-privacy" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">09</span>
              Children's Privacy
            </h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child under 18, we will take steps to delete that information.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 10 */}
          <section id="changes" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">10</span>
              Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on our website with an updated effective date. Your continued use of our services after such changes constitutes your acceptance of the updated policy.
            </p>
          </section>

          <div className="section-divider" />

          {/* Section 11 */}
          <section id="contact-us" className="policy-section">
            <h2 className="policy-section-title">
              <span className="section-number">11</span>
              Contact Us
            </h2>
            <p>If you have questions or concerns about this Privacy Policy or our data practices, please contact us:</p>

            <div className="contact-info-grid">
              <div className="contact-info-item">
                <span className="contact-info-label">Company</span>
                <span className="contact-info-value">All Seasons Landscaping, Inc.</span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Location</span>
                <span className="contact-info-value">Sun Valley, Idaho</span>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Email</span>
                <a href="mailto:matt@allseasons.info" className="contact-info-value policy-link">matt@allseasons.info</a>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">Phone</span>
                <a href="tel:2088068440" className="contact-info-value policy-link">(208) 806-8440</a>
              </div>
              <div className="contact-info-item">
                <span className="contact-info-label">SMS Number</span>
                <a href="tel:18555033293" className="contact-info-value policy-link">(855) 503-3293</a>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
